// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Button, Paper, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Practitioner, PractitionerQualification } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconCheck, IconSquareChevronDown } from '@tabler/icons-react';
import { DataTable, type DataTableSortStatus } from 'mantine-datatable';
import { type JSX, useMemo, useState } from 'react';

import { FormCard } from '../../../components/FormCard';
import { TableAnchor } from '../../../components/layout/TableAnchor';
import {
  APPLICATION_ID_SYSTEM,
  CREDENTIAL_SYSTEM,
  formatDate,
  getQualificationExtension,
  getQualificationFileAttachment,
  getQualificationsBySystem,
} from '../../../utils/practitioner';
import { ITEMS_PER_PAGE, LICENSE_STATUS_ERROR_STATES } from '../constants';
import { CredentialSubmittedModal } from './CredentialSubmittedModal';
import { SubmitCredentialModal } from './SubmitCredentialModal';

interface CredentialsSectionProps {
  practitioner: Practitioner;
  qualifications: PractitionerQualification[];
  onSaved: (updated: Practitioner) => void;
}

type CredentialRow = {
  id: number;
  q: PractitionerQualification;
  payor: string;
  status: string;
  planId: string;
  effectiveDate: string;
  expirationDate: string;
  applicationId: string;
};

export function CredentialsSection({ practitioner, qualifications, onSaved }: CredentialsSectionProps): JSX.Element {
  const medplum = useMedplum();
  const [page, setPage] = useState(1);
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<CredentialRow>>({
    columnAccessor: 'payor',
    direction: 'asc',
  });
  const [modalOpened, setModalOpened] = useState(false);
  const [editingQ, setEditingQ] = useState<PractitionerQualification | null>(null);
  const [submittedQ, setSubmittedQ] = useState<PractitionerQualification | null>(null);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [saving, setSaving] = useState(false);

  const rows: CredentialRow[] = useMemo(
    () =>
      getQualificationsBySystem(qualifications, CREDENTIAL_SYSTEM).map((q, i) => ({
        id: i,
        q,
        payor: q.issuer?.display ?? '',
        status: getQualificationExtension(q, 'status'),
        planId: q.identifier?.[0]?.value ?? '',
        effectiveDate: q.period?.start ?? '',
        expirationDate: q.period?.end ?? '',
        applicationId: q.identifier?.find((id) => id.system === APPLICATION_ID_SYSTEM)?.value ?? '',
      })),
    [qualifications]
  );

  const sortedRows = useMemo(() => {
    const col = sortStatus.columnAccessor as keyof CredentialRow;
    const isDateField = col === 'effectiveDate' || col === 'expirationDate';

    const sorted = [...rows].sort((a, b) => {
      const av = a[col];
      const bv = b[col];

      if (isDateField) {
        const dateA = new Date(String(av)).getTime();
        const dateB = new Date(String(bv)).getTime();
        return sortStatus.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }

      const strA = String(av);
      const strB = String(bv);
      return sortStatus.direction === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });
    return sorted;
  }, [rows, sortStatus]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return sortedRows.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedRows, page]);

  const handleSortStatusChange = (status: DataTableSortStatus<CredentialRow>): void => {
    setPage(1);
    setSortStatus(status);
  };

  const handleSubmitCredential = async (newQualification: PractitionerQualification): Promise<void> => {
    setSaving(true);
    const isEditing = editingQ !== null;
    try {
      const existing = practitioner.qualification ?? [];
      const updatedQualifications = isEditing
        ? existing.map((q) => (q === editingQ ? newQualification : q))
        : [...existing, newQualification];
      const updated: Practitioner = { ...practitioner, qualification: updatedQualifications };
      const result = await medplum.updateResource(updated);
      onSaved(result);
      setModalOpened(false);
      setEditingQ(null);
      if (isEditing) {
        showNotification({
          color: 'green',
          icon: <IconCheck size={16} />,
          message: 'Credential updated successfully',
        });
      } else {
        setSubmittedQ(newQualification);
        setConfirmModalOpened(true);
      }
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <FormCard
        title="Credentials"
        titleOrder={2}
        action={
          <Button size="xs" onClick={() => setModalOpened(true)}>
            Submit New
          </Button>
        }
      >
        {rows.length === 0 ? (
          <Text size="sm" c="dimmed">
            No credentials on file.
          </Text>
        ) : (
          <DataTable
            highlightOnHover
            sortIcons={{
              sorted: <IconSquareChevronDown size={12} stroke={1.5} />,
              unsorted: <IconSquareChevronDown size={12} stroke={1.5} style={{ opacity: 0.7 }} />,
            }}
            tableWrapper={({ children }) => (
              <Paper
                style={{
                  border: '1px solid var(--Border-default, rgba(222, 226, 230, 1))',
                  borderRadius: 8,
                  overflow: 'auto',
                  maxHeight: 350,
                }}
              >
                {children}
              </Paper>
            )}
            styles={{
              header: { backgroundColor: 'white', fontSize: 12, fontWeight: 600, paddingBlock: 8, paddingInline: 20 },
              table: {
                color: 'var(--Text-title, #000)',
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '15px',
                paddingBlock: 8,
                paddingInline: 20,
              },
            }}
            records={pagedRows}
            columns={[
              { accessor: 'payor', title: 'Payor', sortable: true },
              {
                accessor: 'status',
                title: 'Status',
                sortable: true,
                render: ({ status }) => (
                  <span style={{ color: LICENSE_STATUS_ERROR_STATES.includes(status as never) ? 'red' : 'inherit' }}>
                    {status || '—'}
                  </span>
                ),
              },
              { accessor: 'planId', title: 'Plan ID', sortable: true },
              {
                accessor: 'effectiveDate',
                title: 'Effective Date',
                sortable: true,
                render: ({ effectiveDate }) => formatDate(effectiveDate) || '—',
              },
              {
                accessor: 'expirationDate',
                title: 'Expiration Date',
                sortable: true,
                render: ({ expirationDate }) => formatDate(expirationDate) || '—',
              },
              {
                accessor: 'applicationId',
                title: 'Application ID',
                render: ({ applicationId }) => applicationId || '—',
              },
              {
                accessor: 'file',
                title: 'Files',
                render: ({ q }) => {
                  const file = getQualificationFileAttachment(q);
                  return file?.url ? (
                    <TableAnchor href={file.url} size="xs" target="_blank" rel="noopener noreferrer">
                      {file.title ?? file.url.split('/').pop()}
                    </TableAnchor>
                  ) : (
                    '—'
                  );
                },
              },
              {
                accessor: 'actions',
                title: 'Actions',
                render: ({ q }) => (
                  <TableAnchor
                    component="button"
                    size="xs"
                    data-testid="edit-credential-action"
                    onClick={() => {
                      setEditingQ(q);
                      setModalOpened(true);
                    }}
                  >
                    Edit
                  </TableAnchor>
                ),
              },
            ]}
            totalRecords={rows.length}
            recordsPerPage={ITEMS_PER_PAGE}
            paginationWithEdges={false}
            page={page}
            onPageChange={setPage}
            sortStatus={sortStatus}
            onSortStatusChange={handleSortStatusChange}
          />
        )}
      </FormCard>
      <CredentialSubmittedModal
        opened={confirmModalOpened}
        onFinish={() => {
          setConfirmModalOpened(false);
          setSubmittedQ(null);
        }}
        onEdit={() => {
          const matchedQ = qualifications.find(
            (q) =>
              q.code?.coding?.[0]?.system === CREDENTIAL_SYSTEM &&
              q.code?.coding?.[0]?.code === submittedQ?.code?.coding?.[0]?.code &&
              q.identifier?.[0]?.value === submittedQ?.identifier?.[0]?.value
          );
          setConfirmModalOpened(false);
          setSubmittedQ(null);
          setEditingQ(matchedQ ?? submittedQ);
          setModalOpened(true);
        }}
      />
      <SubmitCredentialModal
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          setEditingQ(null);
        }}
        onSubmit={handleSubmitCredential}
        saving={saving}
        initialQualification={editingQ ?? undefined}
      />
    </>
  );
}
